import { useState, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSettings } from '@/context/SettingsContext';
import packageJson from '../../package.json';
import {
  LayoutDashboard, Package, Users, Tag, ShoppingCart, UserCircle,
  Shield, LogOut, FileText, IndianRupee, UserPlus, BookOpen, Receipt,
  ClipboardList,ScrollText,Menu, X, ChevronDown, ChevronRight, Layers,
  CalendarCheck, Wallet, RotateCcw, Settings as SettingsIcon, Star, Plus, User, BarChart3, Store, Database
} from 'lucide-react';

const menuItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', module: 'dashboard' },
  {
    label: 'Sales Management',
    icon: ShoppingCart,
    groupKey: 'sales',
    children: [
      { path: '/sales/new', icon: Plus, label: 'New Sale', module: 'sales', action: 'create' },
      { path: '/purchase/new', icon: Plus, label: 'New Purchase', module: 'purchasing', action: 'create' },
      { path: '/sales', icon: FileText, label: 'Sales', module: 'sales' },
      { path: '/purchases', icon: FileText, label: 'Purchases', module: 'purchasing' },
      { path: '/sales-return', icon: RotateCcw, label: 'Sales Return', module: 'sales' },
      { path: '/purchase-return', icon: RotateCcw, label: 'Purchase Return', module: 'purchasing' },
    ]
  },
  { path: '/customers', icon: UserPlus, label: 'Customers', module: 'customers' },
  { path: '/outlets', icon: Store, label: 'Outlets', module: 'outlets' },
  { path: '/sellers', icon: UserCircle, label: 'Vendors', module: 'sellers' },
  { path: '/ledgers', icon: BookOpen, label: 'Ledgers', module: 'ledgers' },
  { path: '/item-master', icon: Tag, label: 'Item Master', module: 'item_master' },
  {
    label: 'Staff & HR',
    icon: Users,
    groupKey: 'hr',
    children: [
      { path: '/users', icon: Users, label: 'Staff Management', module: 'users' },
      { path: '/roles', icon: Shield, label: 'Roles', module: 'roles' },
      { path: '/attendance', icon: CalendarCheck, label: 'Attendance', module: 'attendance' },
      { path: '/payroll', icon: Wallet, label: 'Payroll', module: 'payroll' },
    ]
  },
  {
   label: 'Salesman Stocks',
    icon: RotateCcw,
    groupKey: 'stockTransfer',
    children: [
      { path: '/salesman/issue-new', icon: Plus, label: 'Issue Jewellery', module: 'salesman_stock', action: 'create' },
      { path: '/salesman/issues', icon: FileText, label: 'Custody History', module: 'salesman_stock' },
      { path: '/stock-transfer/stock/main', icon: Package, label: 'Current Custody', module: 'salesman_stock' },
    ]
  },
  {
    label: 'Stock & Inventory',
    icon: Package,
    groupKey: 'stock',
    children: [
     { path: '/live-stock', icon: Package, label: 'Live Stock', module: 'inventory' },
     { path: '/product-catalog', icon: Layers, label: 'Product Catalog', module: 'inventory' },
     { path: '/inventory', icon: Package, label: 'Inventory', module: 'inventory' },
     { path: '/stock-ledger', icon: ScrollText, label: 'Stock Ledger', module: 'stock_ledger' },
     { path: '/stock-adjustment', icon: ClipboardList, label: 'Stock Adjustment', module: 'stock_adjustment' },
     { path: '/stock-audit', icon: FileText, label: 'Stock Audit', module: 'stock_audit' },
  ]
  },
  { isDivider: true, label: 'Operations' },
  { path: '/outstanding', icon: IndianRupee, label: 'Pending Payments', module: 'accounts' },
  //{ path: '/accounts', icon: IndianRupee, label: 'Accounts', module: 'accounts' },
  { path: '/vouchers', icon: Receipt, label: 'Vouchers', module: 'vouchers' },
  { path: '/reports', icon: BarChart3, label: 'Reports', module: 'reports' },
  { path: '/logs', icon: FileText, label: 'Logs', module: 'logs' },
  { path: '/settings', icon: SettingsIcon, label: 'Settings', module: 'settings' },
  { path: '/backup', icon: Database, label: 'Backup & Export', module: 'backup' },
];

const Sidebar = ({ user, onLogout }) => {
  const { settings } = useSettings();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('bookmarks')) || [];
    } catch {
      return [];
    }
  });

  const [openGroups, setOpenGroups] = useState({
    hr: location.pathname.includes('/users') || location.pathname.includes('/roles') || location.pathname.includes('/attendance') || location.pathname.includes('/payroll'),
    stock: location.pathname.includes('/stock') || location.pathname === '/inventory' || location.pathname === '/categories' || location.pathname === '/item-types',
    sales: location.pathname.includes('/sales') || location.pathname.includes('/sales-return') || location.pathname.includes('/purchase'),
  });

  const toggleGroup = useCallback((group, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpenGroups(prev => ({ ...prev, [group]: !prev[group] }));
  }, []);

  const toggleBookmark = (e, item) => {
    e.preventDefault();
    e.stopPropagation();

    setBookmarks(prev => {
      const isBookmarked = prev.some(b => b.path === item.path);
      let newBookmarks;
      if (isBookmarked) {
        newBookmarks = prev.filter(b => b.path !== item.path);
      } else {
        newBookmarks = [...prev, { path: item.path, label: item.label }];
      }
      localStorage.setItem('bookmarks', JSON.stringify(newBookmarks));
      return newBookmarks;
    });
  };

  const hasPermission = useCallback((module, action = 'view') => {
    if (!user) return false;
    if (module === 'salesman_stock') return true;
    if (module === 'stock_audit') return true;
    if (user.role_name === 'Admin') return true;
    if (!user.permissions) return false;
    const perms = user.permissions[module];
    if (!perms) return false;
    return perms[action] === true;
  }, [user]);

  const filteredMenuItems = useMemo(() => {
    return menuItems.map(item => {
      if (item.children) {
        const visibleChildren = item.children.filter(child => {
          const module = child.module || item.module;
          const action = child.action || 'view';
          return hasPermission(module, action);
        });
        return { ...item, children: visibleChildren };
      }
      return item;
    }).filter(item => {
      if (item.isDivider) return true;
      if (item.children) return item.children.length > 0;
      return hasPermission(item.module, item.action || 'view');
    });
  }, [user, hasPermission]);

  const NavLink = ({ item, onClick, isSubItem = false }) => {
    const Icon = item.icon || Layers;
    const isActive = location.pathname === item.path;
    const isBookmarked = bookmarks.some(b => b.path === item.path);

    return (
      <div className="relative group">
        <Link
          to={item.path}
          onClick={onClick}
          className={`flex items-center gap-3 px-4 py-2 text rounded-sm font-medium tracking-wide transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#d4af37] focus:ring-inset ${isActive
              ? 'bg-[#d4af37] text-[#022c22] shadow-sm'
              : 'text-white/80 hover:bg-white/5 hover:text-white'
            } ${isSubItem ? 'pl-10 py-1.5 text-xs' : 'py-2.5 text-sm'} pr-10`}
        >
          <Icon size={isSubItem ? 16 : 18} strokeWidth={isActive ? 2 : 1.5} />
          <span>{item.label}</span>
        </Link>
        <button
          onClick={(e) => toggleBookmark(e, item)}
          className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#d4af37] ${isBookmarked ? 'opacity-100 text-[#d4af37]' : 'opacity-0 group-hover:opacity-100 text-white/40 hover:text-white'} transition-opacity`}
          title={isBookmarked ? "Remove bookmark" : "Add bookmark"}
        >
          <Star size={14} className={isBookmarked ? "fill-current" : ""} />
        </button>
      </div>
    );
  };

  const NavGroup = ({ label, icon: Icon, isOpen, onToggle, items, groupKey }) => {
    return (
      <div className="pt-1">
        <button
          type="button"
          onClick={(e) => onToggle(groupKey, e)}
          className={`flex items-center justify-between w-full px-4 py-2.5 rounded-sm font-medium transition-all duration-300 ${isOpen ? 'text-[#d4af37]' : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
        >
          <div className="flex items-center gap-3">
            <Icon size={18} strokeWidth={1.5} />
            <span className="text-sm">{label}</span>
          </div>
          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        {isOpen && (
          <div className="mt-0.5 space-y-0.5">
            {items.map((item) => (
              <NavLink key={item.path} item={item} isSubItem={true} onClick={() => setMobileOpen(false)} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const sidebarContentJSX = (
    <>
      <div className="p-4 lg:p-6 border-b border-[#d4af37]/20">
        <h1 className="text-xl lg:text-2xl font-serif font-bold text-[#d4af37]" data-testid="sidebar-title">
          {settings?.company_name || 'Rudraksh 925'}
        </h1>
        <p className="text-xs text-white/70 mt-1">Jewellery ERP v2.0</p>
      </div>

      <nav className="flex-1 p-3 lg:p-4 space-y-0.5 overflow-y-auto custom-scrollbar">
        {filteredMenuItems.map((item, index) => {
          if (item.isDivider) {
            return (
              <div key={`divider-${index}`} className="pt-2 border-t border-[#d4af37]/10 mt-2">
                <div className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-white/20">
                  {item.label}
                </div>
              </div>
            );
          }

          if (item.children) {
            return (
              <NavGroup
                key={item.groupKey}
                label={item.label}
                icon={item.icon}
                isOpen={openGroups[item.groupKey]}
                onToggle={toggleGroup}
                groupKey={item.groupKey}
                items={item.children}
              />
            );
          }

          return <NavLink key={item.path} item={item} onClick={() => setMobileOpen(false)} />;
        })}
      </nav>

      <div className="p-3 lg:p-4 border-t border-[#d4af37]/20">
        <div className="px-4 py-2 mb-2">
          <p className="text-sm font-medium text-white truncate">{user?.name}</p>
          <p className="text-xs text-white/60 truncate">{user?.email}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setMobileOpen(false);
            onLogout();
          }}
          data-testid="logout-button"
          className="flex items-center gap-3 w-full px-4 py-2.5 rounded-sm font-medium tracking-wide text-white/80 hover:bg-white/10 hover:text-white transition-all duration-300"
        >
          <LogOut size={18} strokeWidth={1.5} />
          <span className="text-sm">Logout</span>
        </button>
        <div className="mt-4 px-4 border-t border-white/5 pt-4">
          <p className="text-[10px] text-white/30 uppercase tracking-[0.2em] font-bold">App Version {packageJson.version}</p>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-[60] bg-[#022c22] border-b border-[#d4af37]/20 px-4 py-3 flex items-center justify-between">
        <h1 className="text-lg font-serif font-bold text-[#d4af37]">
          {settings?.company_name || 'Rudraksh 925'}
        </h1>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
          className="text-white/80 hover:text-white p-2"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-[80]"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div className={`lg:hidden fixed top-0 left-0 h-full w-64 bg-[#022c22] z-[100] transform transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } flex flex-col`}>
        {sidebarContentJSX}
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex w-64 bg-[#022c22] text-white h-screen fixed left-0 top-0 z-50 border-r border-[#d4af37]/20 flex-col">
        {sidebarContentJSX}
      </div>

      {/* Desktop Top App Bar (Bookmarks) */}
      <div className="hidden lg:flex fixed top-0 left-64 right-0 h-14 bg-white border-b border-gray-200 z-40 pl-6 pr-32 items-center gap-2 overflow-x-auto shadow-sm">
        {bookmarks.length === 0 ? (
          <span className="text-gray-400 text-sm ml-2 flex items-center gap-1">
            <Star size={14} /> Star items in menu to pin them here
          </span>
        ) : (
          bookmarks.map(b => (
            <Link
              key={b.path}
              to={b.path}
              className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-full text-xs font-medium transition-colors whitespace-nowrap shadow-xs ${location.pathname === b.path
                  ? 'bg-stone-100 border-stone-300 text-stone-800'
                  : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50 hover:border-stone-300'
                }`}
            >
              <Star size={12} className="fill-[#d4af37] text-[#d4af37]" />
              {b.label}
            </Link>
          ))
        )}
      </div>
    </>
  );
};

export default Sidebar;
